// Keyboard shortcuts for the ops photo viewer (SCRUM-408). Pure: given the key
// and the viewer's state, say what to do — the component performs it.
//
//   x   suppress the target match ("it's not her"; Remove for a manual tag)
//   c   confirm the target match ("it's her") — works on a match in review, a
//       suppressed match and a plain auto match alike
//   d   ask to delete the photo (opens the confirm dialog, never instant)
//   ← → previous / next photo, Esc closes
//
// The target match is the selected athlete's row on this photo; with no
// athlete selected, the photo's only match if it has exactly one. Otherwise
// x/c do nothing — the legend says so.

export function targetMatch({ attendeeId, matches }) {
  if (attendeeId) return matches.find((m) => m.attendeeId === attendeeId) ?? null;
  return matches.length === 1 ? matches[0] : null;
}

export function resolveViewerKey(key, { deleted, attendeeId, matches }) {
  if (key === 'ArrowLeft') return { type: 'prev' };
  if (key === 'ArrowRight') return { type: 'next' };
  if (key === 'Escape') return { type: 'close' };
  if (deleted) return null;
  if (key === 'd') return { type: 'delete' };
  if (key !== 'x' && key !== 'c') return null;
  const match = targetMatch({ attendeeId, matches });
  if (!match) return null;
  if (key === 'x') return { type: 'suppress', match, verb: match.manual ? 'Removed' : 'Suppressed' };
  return { type: 'confirm', match, verb: 'Confirmed' };
}

// Which of the four ops buckets a match row is in — the same partition as the
// API's cards (autoMatchWhere / assignedMatchWhere / reviewWhere /
// suppressed), so a tile outline or face box reads like the counter it adds to.
export function matchBucket(m) {
  if (!m) return null;
  if (m.status === 'suppressed') return 'suppressed';
  if (m.status === 'confirmed' || m.source === 'manual' || m.source === 'inferred') return 'manual';
  return m.visible ? 'auto' : 'review';
}

// SCRUM-357: the Review bucket generalises the old "near miss" bucket — it now
// also holds matches flagged adult face / small face / eyes closed. The API
// stores those three reason keys on the match (never "lowScore": a low score
// isn't stored, it's derived here by comparing the auto match's confidence to
// the event's threshold). REASON_LABEL is the exact operator-facing text.
export const REASON_LABEL = { lowScore: 'low score', adultFace: 'adult face', smallFace: 'small face', eyesClosed: 'eyes closed' };

/** Every reason a match is in Review: 'low score' (derived — the API never stores it)
 * when the score is under the event threshold, then the stored reasons, in the
 * order the API returned them. */
export function reviewReasons(m, threshold) {
  const stored = m.reviewReasons ?? [];
  const low = m.status === 'auto' && m.confidenceScore < threshold ? ['lowScore'] : [];
  return [...low, ...stored];
}

// SCRUM-357 (fix wave): the face-box tooltip in EventOps.jsx must apply the
// same "only when actually held" guard MatchChip.jsx applies before calling
// reviewReasons() — a confirmed or suppressed row's stored reviewReasons are
// left untouched (see api match-visibility.ts / matching.service.ts: nothing
// clears them on confirm/suppress), so calling reviewReasons() unconditionally
// renders a stale "small face" chip on a match that is no longer in Review.
// With no live match row at all, fall back to the face's own stored reasons
// (faceOverlay.js) as before.
export function faceReasons(match, faceOwnReasons, threshold) {
  if (!match) return faceOwnReasons ?? [];
  const held = match.status === 'auto' && !match.visible;
  return held ? reviewReasons(match, threshold) : [];
}

// SCRUM-426: the viewer never moves on a decision. The refresh may drop the
// open photo out of the current view (x on an athlete's match takes it off
// her list); the viewer then keeps this patched copy so the operator can
// keep working the other profiles on the same photo. `status` is the API's
// reply: suppressed | confirmed | removed (a manual tag undone). Anything
// else (restore, unknown) returns the photo untouched.
export function patchMatch(photo, attendeeId, status) {
  if (status === 'removed') {
    return { ...photo, matchedAttendees: photo.matchedAttendees.filter((m) => m.attendeeId !== attendeeId) };
  }
  if (status !== 'suppressed' && status !== 'confirmed') return photo;
  return {
    ...photo,
    matchedAttendees: photo.matchedAttendees.map((m) =>
      m.attendeeId === attendeeId ? { ...m, status, visible: status === 'confirmed' } : m,
    ),
  };
}

// SCRUM-466: the decision a click makes, applied locally before the server
// answers so the tile/viewer changes instantly (the venue connection costs
// ~1.3 s per request). `kind` is suppressed | removed (a manual tag undone)
// | confirmed | restored. A restored row goes back to auto: visible when
// its score clears the event threshold and it isn't held for a reason.
export function decisionKind(action, match) {
  if (action === 'suppress') return match?.manual ? 'removed' : 'suppressed';
  if (action === 'confirm') return 'confirmed';
  if (action === 'restore') return 'restored';
  return null;
}

export function applyDecision(photo, attendeeId, kind, threshold) {
  if (kind !== 'restored') return patchMatch(photo, attendeeId, kind);
  return {
    ...photo,
    matchedAttendees: photo.matchedAttendees.map((m) =>
      m.attendeeId === attendeeId
        ? {
            ...m,
            status: 'auto',
            visible: m.confidenceScore >= threshold && !(m.reviewReasons?.length),
          }
        : m,
    ),
  };
}

// SCRUM-436: which actions a grid tile offers — the same set MatchChip shows
// in the viewer, for the filtered athlete's match on that photo. Only with
// one athlete selected (the action needs a subject), never in "As parent
// sees it" (a family gallery spans several athletes) or in Deleted, and a
// suppressed row only where the viewer shows its chip (showSuppressedChips).
export function tileActions(match, { attendeeId, asParent, deleted, showSuppressed }) {
  if (!attendeeId || asParent || deleted || !match) return [];
  if (match.status === 'suppressed') {
    return showSuppressed ? [{ type: 'restore', verb: 'Restored' }] : [];
  }
  const held = match.status === 'auto' && !match.visible;
  const suppress = match.manual
    ? { type: 'suppress', verb: 'Removed', label: 'Remove' }
    : { type: 'suppress', verb: 'Suppressed', label: 'Suppress' };
  return held ? [{ type: 'confirm', verb: 'Confirmed' }, suppress] : [suppress];
}
